// theme/AppTheme.js
import { DefaultTheme as NavigationDefaultTheme, DarkTheme as NavigationDarkTheme } from '@react-navigation/native';

// Light Theme (Default)
export const LightTheme = {
  ...NavigationDefaultTheme,
  colors: {
    ...NavigationDefaultTheme.colors,
    // Primary colors
    primary: '#0e4e96',
    primaryLight: '#4a7db6',
    primaryDark: '#08356d',
    
    // Background colors
    background: '#ffffff',
    backgroundSecondary: '#f8f9fa',
    backgroundTertiary: '#e9ecef',
    
    // Surface colors
    surface: '#ffffff',
    surfaceDisabled: '#f5f5f5',
    
    // Text colors
    text: '#212529',
    textSecondary: '#6c757d',
    textDisabled: '#adb5bd',
    textInverse: '#ffffff',
    
    // Border colors
    border: '#dee2e6',
    borderLight: '#e9ecef',
    borderDark: '#ced4da',
    
    // Status colors
    success: '#28a745',
    warning: '#ffc107',
    error: '#dc3545',
    info: '#17a2b8',
    
    // Notification
    notification: '#FF6B35',
    
    // Accent colors
    accent: '#20c997',
    accentLight: '#3dd5a8',
    accentDark: '#1aa179',
    
    // Business colors
    businessPrimary: '#0e4e96',
    businessSecondary: '#6c757d',
    
    // Professional colors
    professionalPrimary: '#20c997',
    professionalSecondary: '#17a2b8',
    
    // Learning colors
    learningPrimary: '#ff6b35',
    learningSecondary: '#fd7e14',
    
    // Card
    card: '#ffffff',
    
    // Shadows
    shadow: 'rgba(0, 0, 0, 0.1)',
    shadowDark: 'rgba(0, 0, 0, 0.2)',
  },
  fonts: {
    regular: 'System',
    medium: 'System',
    bold: 'System',
    light: 'System',
    thin: 'System',
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },
  borderRadius: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    round: 50,
  },
  fontSize: {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 18,
    xl: 20,
    xxl: 24,
    xxxl: 32,
  },
};

// Dark Theme
export const DarkTheme = {
  ...NavigationDarkTheme,
  colors: {
    ...NavigationDarkTheme.colors,
    // Primary colors
    primary: '#4dabf7',
    primaryLight: '#80c3ff',
    primaryDark: '#1a73e8',
    
    // Background colors
    background: '#121212',
    backgroundSecondary: '#1e1e1e',
    backgroundTertiary: '#2d2d2d',
    
    // Surface colors
    surface: '#1e1e1e',
    surfaceDisabled: '#2d2d2d',
    
    // Text colors
    text: '#ffffff',
    textSecondary: '#b0b0b0',
    textDisabled: '#6d6d6d',
    textInverse: '#000000',
    
    // Border colors
    border: '#333333',
    borderLight: '#404040',
    borderDark: '#262626',
    
    // Status colors
    success: '#34c759',
    warning: '#ff9500',
    error: '#ff3b30',
    info: '#5ac8fa',
    
    // Notification
    notification: '#FF6B35',
    
    // Accent colors
    accent: '#64d2ff',
    accentLight: '#8ae2ff',
    accentDark: '#30c1ff',
    
    // Business colors
    businessPrimary: '#4dabf7',
    businessSecondary: '#6d6d6d',
    
    // Professional colors
    professionalPrimary: '#64d2ff',
    professionalSecondary: '#5ac8fa',
    
    // Learning colors
    learningPrimary: '#ff9500',
    learningSecondary: '#ffa726',
    
    // Card
    card: '#1e1e1e',
    
    // Shadows
    shadow: 'rgba(0, 0, 0, 0.3)',
    shadowDark: 'rgba(0, 0, 0, 0.5)',
  },
  fonts: {
    regular: 'System',
    medium: 'System',
    bold: 'System',
    light: 'System',
    thin: 'System',
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },
  borderRadius: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    round: 50,
  },
  fontSize: {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 18,
    xl: 20,
    xxl: 24,
    xxxl: 32,
  },
};

// Default export
export default {
  LightTheme,
  DarkTheme,
};

// Utility functions for theme usage
export const getThemeColors = (isDarkMode) => {
  return isDarkMode ? DarkTheme.colors : LightTheme.colors;
};

export const getThemeSpacing = (isDarkMode) => {
  return isDarkMode ? DarkTheme.spacing : LightTheme.spacing;
};

export const getThemeBorderRadius = (isDarkMode) => {
  return isDarkMode ? DarkTheme.borderRadius : LightTheme.borderRadius;
};

export const getThemeFontSize = (isDarkMode) => {
  return isDarkMode ? DarkTheme.fontSize : LightTheme.fontSize;
};

// Helper function to create themed styles
export const createThemedStyles = (lightStyles, darkStyles) => {
  return (isDarkMode) => {
    return isDarkMode 
      ? { ...darkStyles, colors: DarkTheme.colors }
      : { ...lightStyles, colors: LightTheme.colors };
  };
};